import type { UserWithRole } from "../../contexts/user/user.context";
import { User } from "../../models/user/user.model";
import supabase from "../../shared/database/supabase";

const SUPABASE_AUTH_STORAGE_KEY = "supabase.auth.token";

const clearStoredAuthSession = () => {
  if (typeof window === "undefined") return;

  window.localStorage.removeItem(SUPABASE_AUTH_STORAGE_KEY);
  window.sessionStorage.removeItem(SUPABASE_AUTH_STORAGE_KEY);
};

export const signUpWithEmail = async (user: User) => {
  try {
    const u = supabase.auth.user() as UserWithRole;
    const { password, name, lastName, nationality = "", phone, displayName } = user;
    const { data, error } = await supabase.auth.update({
      password: password,
      data: {
        name,
        lastName,
        displayName,
        nationality,
        phone
      }
    });

    if (error) {
      return { data: null, error };
    }

    return { data, error: null };
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const updatePassword = async (password: string) => {
  try {
    const { data, error } = await supabase.auth.update({
      password: password
    });

    if (error) {
      return { data: null, error };
    }

    return { data, error: null };
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const recoverPassword = async (email: string) => {
  try {
    const { data, error } = await supabase.auth.api.resetPasswordForEmail(email);

    if (error) {
      return { data: null, error };
    }

    return { data, error: null };
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();

    if (error) {
      return { error };
    }

    return { error: null };
  } catch (err) {
    console.log(err);
    throw err;
  } finally {
    // A stale or invalid JWT can make the remote logout fail. Always remove the
    // persisted access and refresh tokens so the next login starts cleanly.
    clearStoredAuthSession();
  }
};

export const refreshSessionOrSignOut = async () => {
  const { data, user, error } = await supabase.auth.refreshSession();

  if (error || !data) {
    console.error("Unable to refresh the Supabase session. The invalid local session will be cleared.", error);
    await signOut();
    return { data: null, user: null, error };
  }

  return { data, user, error: null };
};

export const signInWithPassword = async (email: string, password: string) => {
  try {
    const { user, error } = await supabase.auth.signIn({
      email,
      password
    });

    if (error) {
      return { data: null, error };
    }
    return { data: user, error: null };
  } catch (err) {
    console.log(err);
    throw err;
  }
};
